import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import type { InputT } from "@patient-watch/types";
import { dictionaryByIdQueryOptions } from "./api/dictionaryQueries";
import { hasOptionRows } from "./inputChoices";

type ChoiceInput = Pick<InputT, "input_id" | "dictionary_id" | "dictionary" | "option_rows">;

function needsRows(input: ChoiceInput): boolean {
    return input.dictionary_id != null && !hasOptionRows(input);
}

/**
 * Some loaders (the form builder, for one) return raw `inputs` rows with a `dictionary_id` but no choices.
 * This fetches each missing dictionary once and attaches `dictionary` and `option_rows`, so the same
 * adapter that serves pages_view can serve them. Inputs that already carry rows are returned untouched.
 */
export function useInputsWithChoices<T extends ChoiceInput>(inputs: T[]): T[] {
    const missingIds = useMemo(
        () => [...new Set(inputs.filter(needsRows).map((input) => input.dictionary_id as string))],
        [inputs]
    );

    const results = useQueries({
        queries: missingIds.map((dictionaryId) => dictionaryByIdQueryOptions(dictionaryId)),
    });

    const loaded = results.map((result) => result.data);

    return useMemo(() => {
        if (missingIds.length === 0) return inputs;
        const byId = new Map(missingIds.map((id, index) => [id, loaded[index]] as const));
        return inputs.map((input) => {
            if (!needsRows(input)) return input;
            const found = byId.get(input.dictionary_id as string);
            if (!found) return input;
            return {
                ...input,
                dictionary: found.dictionary as unknown as T["dictionary"],
                option_rows: found.options as unknown as T["option_rows"],
            };
        });
        // `loaded` is rebuilt every render, so key the memo on the data it holds.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputs, missingIds, ...loaded]);
}

export function useInputWithChoices<T extends ChoiceInput>(input: T): T {
    const list = useMemo(() => [input], [input]);
    return useInputsWithChoices(list)[0] ?? input;
}
